import { useEffect, useState } from 'react';
import { matchPath } from 'react-router';

import { VISITED_NEW_BADGES_KEY } from '@provider-portal/common/LocalStorageKeys';
import { getStorageItem, setStorageItem } from '@provider-portal/common/utils/storage';

interface UseVisitedNewBadgesParams {
  menuItems: { href: string; showNewBadge?: boolean }[];
  pathname: string;
}

/**
 * Hrefs of badged menu items the merchant has already opened, remembered per browser so each
 * "New" badge is only ever shown until its page is visited.
 */
export function useVisitedNewBadges({ menuItems, pathname }: UseVisitedNewBadgesParams): string[] {
  const [visitedHrefs, setVisitedHrefs] = useState(readVisitedHrefs);

  const currentHref = menuItems?.find(
    (item) => item.showNewBadge && matchPath(pathname, { path: item.href }),
  )?.href;

  useEffect(() => {
    if (!currentHref) return;

    setVisitedHrefs((previous) => {
      if (previous.includes(currentHref)) return previous;
      const nextHrefs = [...previous, currentHref];
      setStorageItem(VISITED_NEW_BADGES_KEY, JSON.stringify(nextHrefs));
      return nextHrefs;
    });
  }, [currentHref]);

  return visitedHrefs;
}

function readVisitedHrefs(): string[] {
  try {
    const stored: unknown = JSON.parse(getStorageItem(VISITED_NEW_BADGES_KEY) ?? '[]');
    return Array.isArray(stored)
      ? stored.filter((entry): entry is string => typeof entry === 'string')
      : [];
  } catch (_error: unknown) {
    return [];
  }
}
